import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import type {
  ThematicEntityAttribute,
  NewThematicEntityAttribute,
  UpdateThematicEntityAttribute,
} from "@/types/thematicMaps";

export const useThematicEntityAttributes = (mapId: string | undefined) => {
  const [attributes, setAttributes] = useState<ThematicEntityAttribute[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchAttributes = useCallback(async () => {
    if (!user || !mapId) return;

    try {
      setLoading(true);
      // PostgREST caps at 1000 rows per request — fetch in batches via .range()
      const batchSize = 1000;
      let allData: ThematicEntityAttribute[] = [];
      let from = 0;

      while (true) {
        const { data, error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .select("*")
          .eq("map_id", mapId)
          .order("entity_id", { ascending: true })
          .order("sort_order", { ascending: true })
          .range(from, from + batchSize - 1);

        if (error) throw error;
        const batch = (data || []) as ThematicEntityAttribute[];
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        from += batchSize;
      }

      setAttributes(allData);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Blad pobierania atrybutow";
      toast({ title: "Blad", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createAttribute = useCallback(
    async (
      attrData: Omit<NewThematicEntityAttribute, "user_id" | "map_id">,
    ): Promise<ThematicEntityAttribute | null> => {
      if (!user || !mapId) return null;

      try {
        const { data, error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .insert({
            user_id: user.id,
            map_id: mapId,
            ...attrData,
          })
          .select()
          .single();

        if (error) throw error;

        setAttributes((prev) => [...prev, data as ThematicEntityAttribute]);
        toast({ title: "Sukces", description: "Atrybut dodany" });
        return data as ThematicEntityAttribute;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Blad tworzenia atrybutu";
        toast({ title: "Blad", description: msg, variant: "destructive" });
        return null;
      }
    },
    [user, mapId, toast],
  );

  const createAttributesBulk = useCallback(
    async (
      attrsData: Omit<NewThematicEntityAttribute, "user_id" | "map_id">[],
    ): Promise<ThematicEntityAttribute[]> => {
      if (!user || !mapId || attrsData.length === 0) return [];

      try {
        const rows = attrsData.map((a) => ({
          user_id: user.id,
          map_id: mapId,
          ...a,
        }));

        const { data, error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .insert(rows)
          .select();

        if (error) throw error;

        toast({
          title: "Sukces",
          description: `Dodano ${data.length} atrybutow`,
        });
        await fetchAttributes();
        return data as ThematicEntityAttribute[];
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Blad dodawania atrybutow";
        toast({ title: "Blad", description: msg, variant: "destructive" });
        return [];
      }
    },
    [user, mapId, toast, fetchAttributes],
  );

  const updateAttribute = useCallback(
    async (
      id: string,
      updates: UpdateThematicEntityAttribute,
    ): Promise<boolean> => {
      try {
        const { error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .update(updates)
          .eq("id", id);

        if (error) throw error;

        setAttributes((prev) =>
          prev.map((a) => (a.id === id ? { ...a, ...updates } : a)),
        );
        return true;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Blad aktualizacji atrybutu";
        toast({ title: "Blad", description: msg, variant: "destructive" });
        return false;
      }
    },
    [toast],
  );

  const deleteAttribute = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .delete()
          .eq("id", id);

        if (error) throw error;

        setAttributes((prev) => prev.filter((a) => a.id !== id));
        toast({ title: "Sukces", description: "Atrybut usuniety" });
        return true;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Blad usuwania atrybutu";
        toast({ title: "Blad", description: msg, variant: "destructive" });
        return false;
      }
    },
    [toast],
  );

  const deleteAllAttributes = useCallback(async (): Promise<boolean> => {
    if (!mapId) return false;

    try {
      const { error } = await (supabase as any)
        .from("thematic_entity_attributes")
        .delete()
        .eq("map_id", mapId);

      if (error) throw error;

      const count = attributes.length;
      setAttributes([]);
      toast({
        title: "Sukces",
        description: `Usunieto ${count} atrybutow`,
      });
      return true;
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Blad usuwania atrybutow";
      toast({ title: "Blad", description: msg, variant: "destructive" });
      return false;
    }
  }, [mapId, attributes.length, toast]);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchAttributes();
    }
  }, [user?.id, mapId, fetchAttributes]);

  const { isLive } = useRealtimeSubscription({
    channel: `eav_${mapId || 'none'}`,
    table: 'thematic_entity_attributes',
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(user?.id && mapId),
    onChange: fetchAttributes,
  });

  return {
    attributes,
    loading,
    isLive,
    createAttribute,
    createAttributesBulk,
    updateAttribute,
    deleteAttribute,
    deleteAllAttributes,
    refreshAttributes: fetchAttributes,
  };
};
